import React, { useEffect, useId, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaInbox } from 'react-icons/fa';

import { useATraiter } from './ATraiter';

// La supervision liste les types d'argent, de congé et d'attestation, et se
// filtre par état ; les notes de frais ont leur page, ouverte sur « À valider ».
const LIGNES = [
  { type: 'conge', lien: '/rh/supervision?etat=en_attente' },
  { type: 'avance', lien: '/rh/supervision?etat=en_attente' },
  { type: 'acompte', lien: '/rh/supervision?etat=en_attente' },
  { type: 'pret', lien: '/rh/supervision?etat=en_attente' },
  { type: 'note_frais', lien: '/rh/notes-frais' },
  // Déclarées par le salarié, décidées dans « Feuilles de temps », avec la
  // semaine en regard (Yros/Kavero#16, #220).
  { type: 'heures_sup', lien: '/rh/feuilles-temps?avec_heures_sup=1' },
];

/**
 * La boîte « À traiter » de la barre du haut (Yros/Kavero#416) : le total en
 * attente, et une ligne par type qui mène à la liste déjà filtrée.
 */
const BoiteATraiter = () => {
  const { t } = useTranslation();
  const { compteurs } = useATraiter();
  const { pathname } = useLocation();
  const [ouverte, setOuverte] = useState(false);
  const idPanneau = useId();
  const racine = useRef(null);

  useEffect(() => {
    setOuverte(false);
  }, [pathname]);

  useEffect(() => {
    if (!ouverte) return undefined;
    const fermerSurEchap = (evenement) => {
      if (evenement.key === 'Escape') setOuverte(false);
    };
    const fermerAuClicDehors = (evenement) => {
      if (racine.current && !racine.current.contains(evenement.target)) setOuverte(false);
    };
    document.addEventListener('keydown', fermerSurEchap);
    document.addEventListener('mousedown', fermerAuClicDehors);
    return () => {
      document.removeEventListener('keydown', fermerSurEchap);
      document.removeEventListener('mousedown', fermerAuClicDehors);
    };
  }, [ouverte]);

  if (!compteurs) return null;

  const { total } = compteurs;
  const lignes = LIGNES.filter(({ type }) => compteurs[type] > 0);

  return (
    <div ref={racine} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOuverte((etait) => !etait)}
        aria-expanded={ouverte}
        aria-controls={idPanneau}
        aria-label={total > 0 ? t('aTraiter.boutonAvecTotal', { count: total }) : t('aTraiter.titre')}
        className="flex h-10 items-center gap-2 rounded-full border border-gray-200 bg-white px-4 text-sm text-gray-600 shadow-sm transition-colors hover:border-[--color-primary] hover:text-[--color-primary] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[--color-primary]"
      >
        <FaInbox aria-hidden="true" className="h-4 w-4" />
        <span className="hidden sm:inline">{t('aTraiter.titre')}</span>
        {total > 0 && (
          <span aria-hidden="true" className="min-w-[20px] rounded-full bg-orange-500 px-1.5 text-center text-[11px] font-bold leading-5 text-white">
            {total > 99 ? '99+' : total}
          </span>
        )}
      </button>

      {ouverte && (
        <div
          id={idPanneau}
          role="region"
          aria-label={t('aTraiter.titre')}
          className="absolute right-0 top-12 z-40 w-72 max-w-[calc(100vw-2rem)] rounded-2xl border border-gray-100 bg-white p-2 shadow-xl"
        >
          {lignes.length === 0 ? (
            <p className="px-3 py-4 text-sm text-gray-500">{t('aTraiter.rien')}</p>
          ) : (
            <ul>
              {lignes.map(({ type, lien }) => (
                <li key={type}>
                  <Link
                    to={lien}
                    onClick={() => setOuverte(false)}
                    className="flex min-h-[44px] items-center justify-between gap-3 rounded-lg px-3 py-2 text-sm text-gray-800 hover:bg-[--bg-color] hover:text-[--color-primary]"
                  >
                    <span>{t(`aTraiter.types.${type}`, { count: compteurs[type] })}</span>
                    <span aria-hidden="true" className="text-xs font-medium text-[--color-primary]">
                      {t('aTraiter.ouvrir')}
                    </span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

export default BoiteATraiter;
